import { IFreshdeskTicket } from "@/models/freshdeskTicket/freshdeskTicket.types"
import { FreshdeskTicketParser } from "@/models/freshdeskTicket/freshdeskTicket.parser"
import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"
import {
  SearchFreshdeskContactsQueryVariables,
  SearchFreshdeskContactsQuery,
  GetFreshdeskContactsQuery,
  GetFreshdeskTicketsQuery,
  GetFreshdeskContactQuery,
} from "@/api/hasura/generated/graphql"
import { SignedUrlResponse } from "@/api/backofficeRest/generated/model"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"

type LegacyContact = GetFreshdeskContactQuery["freshdesk_contact"][0]

export interface IFreshdeskService {
  searchLegacyContacts(
    params: SearchFreshdeskContactsQueryVariables,
  ): QueryResult<SearchFreshdeskContactsQuery>
  getLegacyContact(contactId: number): QueryResult<LegacyContact>
  getLegacyContacts(accountNumber: string): QueryResult<string[]>
  getLegacyTickets(contactId: string): QueryResult<IFreshdeskTicket[]>
  getLegacyAttachment(path: string): QueryResult<string | null>
}

class FreshdeskServiceClass implements IFreshdeskService {
  searchLegacyContacts(
    params: SearchFreshdeskContactsQueryVariables,
  ): QueryResult<SearchFreshdeskContactsQuery> {
    return useQuery({
      ...HasuraQueries.account.freshdesk._ctx.searchContacts(params),
      parserFn: (data: SearchFreshdeskContactsQuery) => data,
      useRawResponse: true,
    })
  }
  getLegacyContact(contactId: number): QueryResult<LegacyContact> {
    return useQuery({
      ...HasuraQueries.account.freshdesk._ctx.contact(contactId),
      parserFn: (data: GetFreshdeskContactQuery) => data.freshdesk_contact[0],
      useRawResponse: true,
    })
  }
  getLegacyContacts(accountNumber: string): QueryResult<string[]> {
    return useQuery({
      ...HasuraQueries.account.freshdesk._ctx.contacts(accountNumber),
      parserFn: (data: GetFreshdeskContactsQuery) =>
        data.freshdesk_contact.map((contact) => contact.external_id.toString()),
      placeholderData: [],
      useRawResponse: true,
    })
  }
  getLegacyTickets(contactId: string): QueryResult<IFreshdeskTicket[]> {
    return useQuery({
      ...HasuraQueries.account.freshdesk._ctx.tickets(contactId),
      parserFn: (data: GetFreshdeskTicketsQuery) =>
        data.freshdesk_ticket.map((ticket) =>
          FreshdeskTicketParser.toDomain(ticket.raw_data),
        ),
      placeholderData: [],
      useRawResponse: true,
      enabled: !!contactId,
    })
  }
  getLegacyAttachment(path: string): QueryResult<string | null> {
    return useQuery({
      ...BackofficeRestQueries.account.freshdeskAttachmentSignedUrl(path),
      parserFn: (data: SignedUrlResponse) => data?.signedUrl ?? null,
      placeholderData: null,
      useRawResponse: true,
      enabled: !!path,
    })
  }
}

export const FreshdeskService = new FreshdeskServiceClass()
